import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import ClientGuard from "./components/ClientGuard";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ValidationDataProvider } from "./contexts/ValidationDataContext";
import "./App.css";
import LoginPage from "./pages/login";
import MainLayout from "./layout/mainLayout";
import CallbackPage from "./pages/login/callbackPage";
import CommandCenter from "./pages/commandCenter";
import Welcome from "./pages/welcome";
import ReportPresentationFrame from "./components/ReportPresentationFrame/ReportPresentationFrame";

// Lazy-load heavy page components so sidebar renders instantly on navigation
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const ClientValidations = lazy(() => import("./pages/validations/ClientValidations"));
const ClientDailyValidations = lazy(() => import("./pages/validations"));
const CommandCenterDashboard = lazy(() => import("./pages/commandCenterDashboard"));
const ServerDetails = lazy(() => import("./pages/details"));
const MonthlyDodReport = lazy(() => import("./pages/reports/monthly-dod-report/MonthlyDodReport.jsx"));
const IcPolice = lazy(() => import("./pages/reports/ic-police/IcPolice.jsx"));
const ReportsHub = lazy(() => import("./pages/reports"));
const ReleaseStatus = lazy(() => import("./pages/reports/release-status/ReleaseStatus.jsx"));
const ReportsSettings = lazy(() => import("./pages/reports/settings/ReportsSettings.jsx"));
const MismatchReport = lazy(() => import("./pages/reports/mismatch-report/MismatchReport.jsx"));
const PerformanceReport = lazy(() => import("./pages/reports/team-performance-report/team-performance-report.jsx"));
const ReportBuilder = lazy(() => import("./pages/reports/report-builder/ReportBuilder.tsx"));
const QaDashboard = lazy(() => import("./pages/reports/qa-dashboard/QaDashboard.jsx"));
const DataTeamDashboard = lazy(() => import("./pages/reports/data-team-dashboard/DataTeamDashboard.jsx"));
const KpiDashboard = lazy(() => import("./pages/reports/kpi-dashboard/KpiDashboard.jsx"));
const IncidentFailureAudit = lazy(() => import("./pages/reports/incident-failure-audit/IncidentFailureAudit.jsx"));
const AgentSmithMetrics = lazy(() => import("./pages/agent-smith-metrics/AgentSmithMetrics.jsx"));
const AITools = lazy(() => import("./pages/ai-tools/AITools.jsx"));
const EtlJobsPage = lazy(() => import("./pages/data-validations/EtlJobsPage"));
const GlValidationsPage = lazy(() => import("./pages/data-validations/GlValidationsPage"));
const DataValidationsOverview = lazy(() => import("./pages/data-validations/DataValidationsOverview"));
const AgentSmithOverview = lazy(() => import("./pages/chatbot/agent_smith_overview"));
const QaNeoDashboard = lazy(() => import("./pages/qa-neo/QaNeoDashboard"));
const GwBugFixer = lazy(() => import("./pages/gw-bugfixer/GwBugFixer"));
const IrisIntelligenceDashboard = lazy(() => import("./pages/iris/intelligence"));
const Body = lazy(() => import("./components/Body/Body"));
const SettingsPage = lazy(() => import("./components/SettingsPage/SettingsPage"));

function GwBugFixerGuard() {
  const { isAgentSmith } = useAuth();
  if (!isAgentSmith) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<div style={{ padding: "2rem" }} />}>
      <GwBugFixer />
    </Suspense>
  );
}

function AgentSmithGuard() {
  const { isAgentSmith } = useAuth();
  if (!isAgentSmith) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<div style={{ padding: "2rem" }} />}>
      <AgentSmithOverview />
    </Suspense>
  );
}

function AIToolsGuard() {
  const { isAgentSmith } = useAuth();
  if (!isAgentSmith) return <Navigate to="/" replace />;
  return <AITools />;
}

function readStoredDarkMode() {
  try {
    return JSON.parse(localStorage.getItem("darkMode") || "false") === true;
  } catch {
    return false;
  }
}

function TransformTeamGuard({ children }) {
  const { hasTransformDashboard } = useAuth();
  if (!hasTransformDashboard) return <Navigate to="/reports" replace />;
  return children;
}

function TransformTeamOnlyGuard({ children }) {
  const { isTransformTeam } = useAuth();
  if (!isTransformTeam) return <Navigate to="/reports" replace />;
  return children;
}

function AppContent() {
  const [darkMode, setDarkMode] = useState(() => {
    return readStoredDarkMode();
  });

  useEffect(() => {
    const theme = darkMode ? "dark" : "light";
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  return (
    <Router>
      <div className={`App${darkMode ? " option2-dark-bg" : ""}`}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/control" element={<CommandCenter />} />
            <Route path="/" element={<Navigate to="/control" replace />} />
            <Route
              path="/"
              element={
                <MainLayout
                  darkMode={darkMode}
                  setDarkMode={setDarkMode}
                />
              }
            >
              <Route
                path="client/:clientId"
                element={
                  <ClientGuard>
                    <ClientDashboard />
                  </ClientGuard>
                }
              />
              <Route
                path="details/:client"
                element={
                  <ClientGuard>
                    <ServerDetails darkMode={darkMode} />
                  </ClientGuard>
                }
              />
              <Route
                path="validations"
                element={<ClientDailyValidations darkMode={darkMode} />}
              />
              <Route
                path="command-center"
                element={<CommandCenterDashboard />}
              />
              <Route
                path="validations/:client/:env"
                element={
                  <ClientGuard>
                    <ClientValidations />
                  </ClientGuard>
                }
              />
              <Route
                path="validations/:client"
                element={
                  <ClientGuard>
                    <ClientValidations />
                  </ClientGuard>
                }
              />
              <Route
                path="dashboardOriginal"
                element={
                  <Body darkMode={darkMode} setDarkMode={setDarkMode} />
                }
              />
              <Route
                path="reports/dod"
                element={<ReportPresentationFrame><MonthlyDodReport /></ReportPresentationFrame>}
              />
              <Route
                path="reports/ic-police"
                element={<ReportPresentationFrame><IcPolice /></ReportPresentationFrame>}
              />
              <Route
                path="reports"
                element={<ReportsHub />}
              />
              <Route
                path="reports/release-status/:client"
                element={
                  <ReportPresentationFrame>
                    <ClientGuard><ReleaseStatus /></ClientGuard>
                  </ReportPresentationFrame>
                }
              />
              <Route
                path="settings"
                element={
                  <ReportsSettings
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                  />
                }
              />
              <Route
                path="reports/mismatch-report"
                element={<ReportPresentationFrame><MismatchReport /></ReportPresentationFrame>}
              />
              <Route
                path="reports/team-performance"
                element={<ReportPresentationFrame><PerformanceReport /></ReportPresentationFrame>}
              />
              <Route
                path="reports/report-builder"
                element={<ReportBuilder />}
              />
              <Route
                path="reports/qa-dashboard"
                element={
                  <ReportPresentationFrame>
                    <TransformTeamGuard><QaDashboard /></TransformTeamGuard>
                  </ReportPresentationFrame>
                }
              />
              <Route
                path="reports/data-team-dashboard"
                element={
                  <ReportPresentationFrame>
                    <TransformTeamGuard><DataTeamDashboard /></TransformTeamGuard>
                  </ReportPresentationFrame>
                }
              />
              <Route
                path="reports/kpi-dashboard"
                element={
                  <ReportPresentationFrame>
                    <TransformTeamOnlyGuard><KpiDashboard /></TransformTeamOnlyGuard>
                  </ReportPresentationFrame>
                }
              />
              <Route
                path="reports/incident-failure-audit"
                element={
                  <ReportPresentationFrame>
                    <TransformTeamOnlyGuard><IncidentFailureAudit /></TransformTeamOnlyGuard>
                  </ReportPresentationFrame>
                }
              />
              <Route
                path="reports/agent-smith-metrics"
                element={
                  <TransformTeamGuard>
                    <AgentSmithMetrics />
                  </TransformTeamGuard>
                }
              />
              <Route
                path="data-validations"
                element={<DataValidationsOverview />}
              />
              <Route
                path="data-validations/etl-jobs"
                element={<EtlJobsPage />}
              />
              <Route path="iris" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/hub" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/incidents" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/analysis" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/connectors" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/trigger" element={<Navigate to="/iris/intelligence" replace />} />
              <Route path="iris/intelligence" element={<IrisIntelligenceDashboard />} />
              <Route
                path="data-validations/gl-validations"
                element={<GlValidationsPage />}
              />
              <Route
                path="/agent-smith"
                element={<AgentSmithGuard />}
              />
              <Route
                path="/qa-neo"
                element={<QaNeoDashboard />}
              />
              <Route
                path="/ai-tools"
                element={
                  <Suspense fallback={<div style={{ padding: "2rem" }} />}>
                    <AIToolsGuard />
                  </Suspense>
                }
              />
              <Route
                path="/gw-bugfixer"
                element={<GwBugFixerGuard />}
              />
              <Route
                path="settingsOriginal"
                element={
                  <SettingsPage
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                  />
                }
              />
            </Route>
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <ValidationDataProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ValidationDataProvider>
  );
}

export default App;

